import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag, ArrowRight, Lock, Gift } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { launchShiprocketCheckout } from "@/lib/shiprocket";
import { computeBogo, BOGO_LABELS } from "@/lib/bogo";
import { computeBundles } from "@/lib/bundles";
import { toast } from "sonner";
import { GiftRevealCard } from "@/components/GiftRevealCard";

/**
 * Slide-out mini cart drawer — opens from the right when the user clicks
 * the cart icon in the header. Checkout button launches Shiprocket Fastrr
 * one-click checkout drawer.
 */
const CartDrawer = () => {
  const {
    cart,
    cartCount,
    cartTotal,
    cartMrpTotal,
    updateQty,
    removeFromCart,
    clearCart,
    drawerOpen,
    setDrawerOpen,
  } = useStore();

  const savings = Math.max(0, cartMrpTotal - cartTotal);
  const bogo = computeBogo(cart);
  const bundles = computeBundles(cart);
  const anyActiveBundle = bundles.summaries.length > 0;
  // Bundles override BOGO entirely (new offer model)
  const effectiveDiscount = anyActiveBundle ? bundles.totalDiscount : bogo.discount;
  const cartTotalAfterBogo = Math.max(0, cartTotal - effectiveDiscount);
  const shipping = cartTotalAfterBogo >= 499 || cartTotalAfterBogo === 0 ? 0 : 49;
  const total = cartTotalAfterBogo + shipping;

  // Reward tiers (subtle nudges below BOGO box)
  const FREE_SHIP_TARGET = 499;
  const FREE_GIFT_TARGET = 699;
  const shipFree = cartTotalAfterBogo >= FREE_SHIP_TARGET;
  const giftUnlocked = cartTotalAfterBogo >= FREE_GIFT_TARGET;

  // ─── Celebration confetti: fires whenever a NEW BOGO tier unlocks ───
  const [confettiKey, setConfettiKey] = useState(0);
  const prevTierRef = useRef(bogo.tier);
  const prevFreeUnitsRef = useRef(bogo.freeUnits);
  const [savingToast, setSavingToast] = useState(null); // { amount, key }
  useEffect(() => {
    // Fire on either (a) tier change (b1g1 → b1g2) OR (b) more free units within same tier
    const tierChanged = bogo.tier && bogo.tier !== prevTierRef.current;
    const gainedFreebies = bogo.freeUnits > prevFreeUnitsRef.current;
    if (tierChanged || gainedFreebies) {
      setConfettiKey((k) => k + 1);
      if (bogo.discount > 0) {
        setSavingToast({ amount: bogo.discount, key: Date.now() });
      }
    }
    prevTierRef.current = bogo.tier;
    prevFreeUnitsRef.current = bogo.freeUnits;
  }, [bogo.tier, bogo.freeUnits, bogo.discount]);

  // Auto-dismiss the "You saved" toast after 3.2s
  useEffect(() => {
    if (!savingToast) return;
    const t = setTimeout(() => setSavingToast(null), 3200);
    return () => clearTimeout(t);
  }, [savingToast]);

  const handleCheckout = async () => {
    try {
      toast.loading("Opening secure checkout…", { id: "sr-checkout" });
      await launchShiprocketCheckout({ cart });
      toast.dismiss("sr-checkout");
      setDrawerOpen(false);
    } catch (e) {
      toast.dismiss("sr-checkout");
      toast.error(e.message || "Checkout couldn't open. Please try again.");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setDrawerOpen(false)}
        className={`fixed inset-0 bg-plum/40 backdrop-blur-[2px] z-[70] transition-opacity duration-300 ${
          drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
        data-testid="cart-drawer-backdrop"
      />

      {/* You-saved micro-toast — fires when a BOGO tier unlocks / gains free units */}
      {savingToast && drawerOpen && (
        <div
          key={savingToast.key}
          data-testid="cart-saved-toast"
          className="fixed z-[95] bottom-6 left-1/2 -translate-x-1/2 sm:bottom-auto sm:top-6 sm:right-[440px] sm:left-auto sm:translate-x-0 pointer-events-none"
        >
          <style>{`
            @keyframes ggSavedIn {
              0%   { transform: translate(-50%, 40px) scale(0.85); opacity: 0; }
              20%  { transform: translate(-50%, 0) scale(1.05); opacity: 1; }
              100% { transform: translate(-50%, 0) scale(1); opacity: 1; }
            }
            @keyframes ggSavedInDesktop {
              0%   { transform: translateY(-20px) scale(0.9); opacity: 0; }
              20%  { transform: translateY(0) scale(1.05); opacity: 1; }
              100% { transform: translateY(0) scale(1); opacity: 1; }
            }
            .gg-saved-toast-mobile { animation: ggSavedIn 3.2s cubic-bezier(0.2,0.9,0.35,1) both; }
            @media (min-width: 640px) {
              .gg-saved-toast-mobile { animation: ggSavedInDesktop 3.2s cubic-bezier(0.2,0.9,0.35,1) both; }
            }
          `}</style>
          <div
            className="gg-saved-toast-mobile inline-flex items-center gap-3 pl-4 pr-5 py-3 rounded-full"
            style={{
              background: "linear-gradient(105deg,#FBEED6 0%,#F4D58D 55%,#E0BC76 100%)",
              boxShadow:
                "0 24px 48px -18px rgba(74,44,53,0.45), 0 6px 16px -6px rgba(197,152,90,0.55), inset 0 0 0 1px rgba(197,152,90,0.55)",
            }}
          >
            <span className="text-lg">✨</span>
            <span className="text-[11px] tracking-[0.25em] uppercase font-semibold text-plum">
              You saved
            </span>
            <span
              className="font-serif italic text-plum text-lg leading-none"
              style={{ textShadow: "0 1px 0 rgba(255,255,255,0.6)" }}
            >
              ₹{savingToast.amount}
            </span>
            <span className="text-lg">🎉</span>
          </div>
        </div>
      )}

      {/* Drawer panel */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-cream z-[71] shadow-2xl flex flex-col transition-transform duration-400 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-label="Shopping bag"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-sectionbreak flex-shrink-0">
          <div>
            <p className="overline">Your bag</p>
            <h2 className="font-serif text-plum text-2xl leading-none mt-1">
              {cartCount > 0 ? `${cartCount} ${cartCount === 1 ? "piece" : "pieces"}` : "Empty"}
            </h2>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="w-9 h-9 rounded-full hover:bg-sectionbreak/60 text-plum flex items-center justify-center transition-colors"
            aria-label="Close bag"
            data-testid="cart-drawer-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Bundle summary cards — one per active bundle group ── */}
        {anyActiveBundle && (
          <div className="mx-5 mt-4 flex-shrink-0 space-y-2" data-testid="cart-bundle-bar">
            {bundles.summaries.map((b) => {
              const isDream = b.bundleId === "DREAM1199";
              const isGlam = b.bundleId === "GLAM799";
              const bg = isDream
                ? "linear-gradient(105deg,#F0DFE6 0%,#E8CBD6 100%)"
                : isGlam
                  ? "linear-gradient(105deg,#FBEED6 0%,#F0D9AE 100%)"
                  : "linear-gradient(105deg,#FBE8EF 0%,#F6D6E1 100%)";
              const accent = isDream ? "#4A2C35" : isGlam ? "#C5985A" : "#D4537E";
              return (
                <div
                  key={b.bundleId}
                  data-testid={`cart-bundle-${b.bundleId}`}
                  className="rounded-[14px] px-4 py-3.5 relative overflow-hidden"
                  style={{ background: bg, border: `1px solid ${accent}44` }}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[11px] tracking-[0.24em] uppercase font-semibold text-plum">
                      📦 {b.bundleName}
                    </span>
                    <span className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: accent }}>
                      {b.itemCount}/{b.bundleQty} · ₹{b.bundlePrice}
                    </span>
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.6)" }}>
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        width: `${Math.min(100, (b.itemCount / b.bundleQty) * 100)}%`,
                        background: accent,
                      }}
                    />
                  </div>
                  {b.complete ? (
                    <p className="mt-2 font-serif italic text-[12px] text-plum/80">
                      {b.bundleHasGift ? "Dream Box unlocked · Free Gift included" : "Bundle unlocked · Price locked"}
                    </p>
                  ) : (
                    <p className="mt-2 font-serif italic text-[12px] text-plum/70">
                      {b.bundleQty - b.itemCount} more to lock in ₹{b.bundlePrice}
                    </p>
                  )}
                </div>
              );
            })}
            {/* Assured Free Gift line (visual only) */}
            {bundles.hasActiveGift && <GiftRevealCard />}
          </div>
        )}

        {/* ── BOGO Celebration Bars — one card per active tier (gold + rose) ── */}
        {!anyActiveBundle && cart.length > 0 && (bogo.tiers.length > 0 || bogo.nextTier) && (
          <div className="mx-5 mt-4 flex-shrink-0 space-y-2" data-testid="cart-bogo-bar">
            {(bogo.tiers.length > 0
              ? bogo.tiers
              : [{
                  tier: bogo.nextTier,
                  freeUnits: 0,
                  unitsToNext: bogo.unitsToNext,
                  progressPct: bogo.progressPct,
                  unlocked: false,
                }]
            ).map((t, idx) => {
              const isRose = t.tier === "b1g2";
              const cardBg = isRose
                ? "linear-gradient(105deg, #FBE8EF 0%, #F6D6E1 55%, #F1C4D3 100%)"
                : "linear-gradient(105deg, #FBEED6 0%, #F6E4C0 55%, #F0D9AE 100%)";
              const cardBorder = isRose ? "rgba(196,72,120,0.35)" : "rgba(197,152,90,0.4)";
              const progressBg = isRose
                ? "linear-gradient(90deg, #D4537E 0%, #E9A8BE 55%, #C44878 100%)"
                : "linear-gradient(90deg, #C5985A 0%, #F4D58D 55%, #E0BC76 100%)";
              const trackBg = isRose ? "rgba(255,255,255,0.55)" : "rgba(253,246,240,0.5)";
              return (
                <div
                  key={t.tier + idx}
                  data-testid={`cart-bogo-box-${t.tier}`}
                  className="rounded-[14px] px-4 py-3.5 relative overflow-hidden"
                  style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  {/* In-box drifting mini icons (only on first/headline card to avoid clutter) */}
                  {idx === 0 && (
                    <div
                      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[14px]"
                      aria-hidden
                    >
                      <style>{`
                        @keyframes ggBoxDrift {
                          0%   { transform: translate(0, 6px) rotate(-8deg); opacity: 0; }
                          20%  { opacity: 0.9; }
                          100% { transform: translate(var(--dx, 8px), -14px) rotate(12deg); opacity: 0; }
                        }
                        @keyframes ggBoxTwinkle {
                          0%, 100% { opacity: 0.35; transform: scale(0.85); }
                          50%      { opacity: 0.9;  transform: scale(1.15); }
                        }
                        .gg-box-bit    { position: absolute; font-size: 11px; line-height: 1; filter: drop-shadow(0 1px 1px rgba(139,106,52,0.35)); animation: ggBoxDrift 3.6s ease-in-out infinite; }
                        .gg-box-twinkle{ position: absolute; font-size: 10px; line-height: 1; animation: ggBoxTwinkle 2.4s ease-in-out infinite; }
                      `}</style>
                      {[
                        { c: "🎁", left: "18%", top: "22%", d: 0,   dx: 6 },
                        { c: "✨", left: "34%", top: "56%", d: 0.4, dx: -4 },
                        { c: "⭐", left: "48%", top: "18%", d: 0.9, dx: 5 },
                        { c: "🎀", left: "62%", top: "62%", d: 1.4, dx: -6 },
                        { c: "✨", left: "78%", top: "28%", d: 0.7, dx: 4 },
                        { c: "🎁", left: "88%", top: "58%", d: 1.9, dx: -5 },
                      ].map((p, i) => (
                        <span key={i} className="gg-box-bit"
                          style={{ left: p.left, top: p.top, animationDelay: `${p.d}s`, ["--dx"]: `${p.dx}px` }}>
                          {p.c}
                        </span>
                      ))}
                      {[
                        { left: "10%", top: "72%", d: 0.2 },
                        { left: "44%", top: "38%", d: 1.1 },
                        { left: "70%", top: "78%", d: 0.6 },
                        { left: "82%", top: "12%", d: 1.5 },
                      ].map((p, i) => (
                        <span key={`t${i}`} className="gg-box-twinkle"
                          style={{ left: p.left, top: p.top, animationDelay: `${p.d}s`, color: isRose ? "#D4537E" : "#C5985A" }}>
                          ✦
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Tier-unlock replay burst on the headline box */}
                  {idx === 0 && confettiKey > 0 && (
                    <div
                      key={confettiKey}
                      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[14px]"
                      aria-hidden
                    >
                      <style>{`
                        @keyframes ggBoxBurst {
                          0%   { transform: translate(0, 0) scale(0.6); opacity: 0; }
                          15%  { opacity: 1; }
                          100% { transform: translate(var(--bx,0), -18px) scale(1.2); opacity: 0; }
                        }
                        .gg-box-burst { position: absolute; font-size: 12px; animation: ggBoxBurst 1.4s ease-out forwards; }
                      `}</style>
                      {[
                        { c: "✨", left: "20%", bx: -8 },
                        { c: "🎉", left: "38%", bx: 4 },
                        { c: "⭐", left: "55%", bx: -6 },
                        { c: "✨", left: "70%", bx: 8 },
                        { c: "🎁", left: "86%", bx: -4 },
                      ].map((b, i) => (
                        <span key={i} className="gg-box-burst"
                          style={{ left: b.left, bottom: "8px", animationDelay: `${i * 0.08}s`, ["--bx"]: `${b.bx}px` }}>
                          {b.c}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="relative flex items-center justify-between gap-3 mb-2">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      <Gift size={15} className="text-plum flex-shrink-0" />
                      <span className="text-[11px] tracking-[0.24em] uppercase font-semibold text-plum truncate">
                        {BOGO_LABELS[t.tier]}
                      </span>
                    </div>
                    <span className="text-[11px] tracking-[0.24em] uppercase font-semibold text-plum whitespace-nowrap">
                      {t.unlocked
                        ? `+ ${t.freeUnits} FREE`
                        : `${t.unitsToNext} more to unlock`}
                    </span>
                  </div>

                  <p className="relative font-serif italic text-[12px] text-plum/70 leading-tight mb-2">
                    {t.unlocked
                      ? `Offer locked in ✧ Add ${t.unitsToNext} more to unlock the next one`
                      : t.unitsToNext === 1
                        ? "Add 1 more piece & it’s yours, free"
                        : `Add ${t.unitsToNext} more pieces & one is yours, free`}
                  </p>

                  <div
                    className="relative h-1 rounded-full overflow-hidden"
                    style={{ background: trackBg }}
                  >
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${t.progressPct}%`, background: progressBg }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Thin gold hairline nudges (shipping + free gift) ── */}
        {cart.length > 0 && (
          <div
            className="mx-5 mt-2 flex-shrink-0 flex items-center justify-between gap-3 text-[10px] tracking-[0.22em] uppercase"
            data-testid="cart-reward-nudges"
          >
            {/* Free Shipping @ ₹499 */}
            <span
              className={`inline-flex items-center gap-1.5 ${shipFree ? "text-blush font-semibold" : "text-plum/55"}`}
              data-testid="cart-nudge-shipping"
            >
              <span
                className="inline-block h-px w-4"
                style={{
                  background: shipFree
                    ? "linear-gradient(90deg,#D4537E,#C5985A)"
                    : "rgba(197,152,90,0.4)",
                }}
              />
              {shipFree
                ? "✦ Free shipping unlocked"
                : `₹${FREE_SHIP_TARGET - cartTotalAfterBogo} to free ship`}
            </span>

            {/* Free Gift @ ₹699 */}
            <span
              className={`inline-flex items-center gap-1.5 ${giftUnlocked ? "text-blush font-semibold" : "text-plum/55"}`}
              data-testid="cart-nudge-gift"
            >
              {giftUnlocked
                ? "✦ Free gift yours"
                : `₹${FREE_GIFT_TARGET - cartTotalAfterBogo} to free gift`}
              <span
                className="inline-block h-px w-4"
                style={{
                  background: giftUnlocked
                    ? "linear-gradient(90deg,#C5985A,#D4537E)"
                    : "rgba(197,152,90,0.4)",
                }}
              />
            </span>
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="w-16 h-16 rounded-full bg-sectionbreak flex items-center justify-center mb-5">
                <ShoppingBag size={22} className="text-plum/60" />
              </div>
              <p className="font-serif text-2xl text-plum italic mb-2">Your bag is empty</p>
              <p className="text-plum/60 text-sm mb-6 max-w-[260px]">
                Add a few pieces to begin your edit.
              </p>
              <Link
                to="/shop"
                onClick={() => setDrawerOpen(false)}
                className="btn-primary"
                data-testid="cart-drawer-shop-cta"
              >
                Shop the collection
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-sectionbreak">
              {cart.map((item) => {
                const freeQty = bogo.freeUnitIdCounts[item.id] || 0;
                return (
                <li
                  key={item.id}
                  className="py-4 flex gap-4"
                  data-testid={`cart-drawer-item-${item.id}`}
                >
                  <Link
                    to={`/product/${item.id}`}
                    onClick={() => setDrawerOpen(false)}
                    className="w-20 h-24 rounded-xl overflow-hidden bg-pearl flex-shrink-0 relative"
                  >
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    )}
                    {freeQty > 0 && (
                      <span
                        className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[8.5px] tracking-[0.18em] uppercase font-bold text-plum leading-none"
                        style={{
                          background: "linear-gradient(135deg, #F4D58D 0%, #C5985A 100%)",
                        }}
                        data-testid={`cart-item-free-pill-${item.id}`}
                      >
                        Free × {freeQty}
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/product/${item.id}`}
                      onClick={() => setDrawerOpen(false)}
                      className="font-serif text-plum text-[15px] leading-tight line-clamp-2 hover:text-blush transition-colors"
                    >
                      {item.name}
                    </Link>
                    <p className="text-plum text-sm font-medium mt-1.5">
                      ₹{item.price * item.qty}
                      {item.mrp && item.mrp > item.price && (
                        <span className="text-plum/40 line-through text-xs ml-2">
                          ₹{item.mrp * item.qty}
                        </span>
                      )}
                      {freeQty > 0 && (
                        <span className="text-blush text-[11px] font-semibold ml-2 tracking-wide">
                          − ₹{item.price * freeQty} off
                        </span>
                      )}
                    </p>
                    <div className="flex items-center justify-between mt-2.5">
                      <div className="flex items-center border border-gold/40 rounded-full">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="w-7 h-7 flex items-center justify-center text-plum hover:text-blush"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={11} />
                        </button>
                        <span className="w-7 text-center text-xs text-plum">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="w-7 h-7 flex items-center justify-center text-plum hover:text-blush"
                          aria-label="Increase quantity"
                        >
                          <Plus size={11} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-[11px] tracking-wider uppercase text-plum/55 hover:text-blush transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer / Checkout */}
        {cart.length > 0 && (
          <div className="border-t border-sectionbreak px-5 pt-4 pb-5 flex-shrink-0 bg-cream">
            <div className="space-y-1 text-sm mb-4">
              <div className="flex justify-between text-plum/75">
                <span>Subtotal</span>
                <span>₹{cartTotal}</span>
              </div>
              {bogo.discount > 0 && !anyActiveBundle && (
                <div className="flex justify-between text-blush font-medium" data-testid="cart-bogo-discount-row">
                  <span>{BOGO_LABELS[bogo.tier]} · {bogo.freeUnits} free</span>
                  <span>-₹{bogo.discount}</span>
                </div>
              )}
              {bundles.totalDiscount > 0 && (
                <div className="flex justify-between text-blush font-medium" data-testid="cart-bundle-discount-row">
                  <span>Bundle Discount</span>
                  <span>-₹{bundles.totalDiscount}</span>
                </div>
              )}
              {savings > 0 && !anyActiveBundle && (
                <div className="flex justify-between text-blush">
                  <span>Product savings</span>
                  <span>-₹{savings}</span>
                </div>
              )}
              <div className="flex justify-between text-plum/75">
                <span>Shipping</span>
                <span>{shipping === 0 ? "Free" : `₹${shipping}`}</span>
              </div>
              <div className="flex justify-between text-plum font-medium font-serif text-lg pt-2 border-t border-sectionbreak/60">
                <span>Total</span>
                <span>₹{total}</span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="btn-primary w-full"
              data-testid="cart-drawer-checkout"
            >
              <Lock size={13} /> Checkout — 1 click
              <ArrowRight size={14} />
            </button>
            <div className="flex items-center justify-between mt-3">
              <p className="text-[10px] tracking-wider uppercase text-plum/55">
                ✦ Secure · UPI · Cards · COD
              </p>
              <button
                onClick={() => clearCart()}
                className="text-[10px] tracking-wider uppercase text-plum/45 hover:text-blush transition-colors"
                data-testid="cart-drawer-clear"
              >
                Clear bag
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
